import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const base = clsx(
  'inline-flex items-center justify-center gap-2',
  'rounded-md border border-transparent',
  // Design system button label: IBM Plex 400, 20px line-height, -0.15px tracking.
  'font-normal leading-5 tracking-[-0.15px] whitespace-nowrap select-none',
  'cursor-pointer',
  'transition-[background-color,border-color,color] duration-fast ease-standard',
  'disabled:cursor-not-allowed disabled:opacity-55',
);

const sizes: Record<ButtonSize, string> = {
  sm: 'h-7 px-[10px] text-sm',
  md: 'h-9 px-[14px] text-md',
  lg: 'h-[42px] px-[18px] text-base',
};

const variants: Record<ButtonVariant, string> = {
  primary: clsx(
    'bg-brand-primary text-brand-on-primary',
    'enabled:hover:bg-brand-primary-hover',
    'enabled:active:bg-brand-primary-active',
  ),
  outline: clsx(
    'bg-surface-card text-text-primary border-border',
    'enabled:hover:bg-ink-50 enabled:hover:border-border-strong',
  ),
  ghost: clsx('bg-transparent text-text-primary', 'enabled:hover:bg-ink-100'),
  subtle: clsx('bg-purple-50 text-brand-primary', 'enabled:hover:bg-purple-100'),
};

export function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      data-variant={variant}
      data-size={size}
      className={clsx(base, sizes[size], variants[variant], fullWidth && 'w-full', className)}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}
